/**
 * @file Insight to display billable hours.
 * @author Maarten Lensink
 * @customer Rembrandthuis
 * @issue 3644
 */
import * as tslib_1 from "tslib";
import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { DomainService } from '../../domain.service';
import { ResourceService } from '../../resource.service';
import { RoutesService } from '../../routes.service';
import { AlertsService } from '../../core/alerts.service';
import { LocalisationService } from '../../core/localisation.service';
import { ComponentRegistry } from '../../core/component-registry';
import { addZero } from '../../base/helpers/addZero';
import { InsightTextComponent } from '../insight-text/insight-text.component';
import { InsightProvider } from '../insight-provider';
import { InsightService } from '../insight.service';
import { Router, NavigationEnd } from '@angular/router';
import { EventsService } from '../../core/event.service';
/**
 * The insight displays the billable hours based on the actual spent times.
 * @export
 * @class InsightBillableHoursComponent
 * @extends {InsightTextComponent}
 */
var InsightBillableHoursComponent = /** @class */ (function (_super) {
    tslib_1.__extends(InsightBillableHoursComponent, _super);
    /**
     * Creates an instance of InsightBillableHoursComponent.
     * @param {ResourceService} resourceService
     * @param {InsightProvider} provider
     * @param {InsightService} insightService
     * @param {LocalisationService} localisationService
     * @param {ActivatedRoute} route is used to obtain the assignment key.
     * @memberOf InsightBillableHoursComponent
     */
    function InsightBillableHoursComponent(resourceService, provider, insightService, localisationService, alertsService, domainService, translateService, router, eventsService) {
        var _this = _super.call(this, resourceService, provider, insightService, localisationService, alertsService, eventsService) || this;
        _this.domainService = domainService;
        _this.translateService = translateService;
        _this.router = router;
        /**
         * The factor is multiplied with the actual spent times.
         * @private
         * @memberOf InsightBillableHoursComponent
         */
        _this.billableFactor = 1.25;
        /**
         * The assignment key.
         * @private
         * @memberOf InsightBillableHoursComponent
         */
        _this._entityKey = null;
        _this.subscriptionPool.add(_this.router.events.subscribe(function (event) {
            if (event instanceof NavigationEnd) {
                _this.afterInitialize();
            }
        }));
        return _this;
    }
    Object.defineProperty(InsightBillableHoursComponent.prototype, "assignmentKey", {
        /**
         * The query parameter for the view contains the key of the assignment.
         * The parameter is not used if the entity is a SpendTime.
         * @memberOf InsightBillableHoursComponent
         */
        get: function () {
            return this._entityKey;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InsightBillableHoursComponent.prototype, "start", {
        /**
         * The query parameter for the view needs to not exclude the assignment and is therefore safely early.
         * @memberOf InsightBillableHoursComponent
         */
        get: function () {
            return new Date(0);
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(InsightBillableHoursComponent.prototype, "finish", {
        /**
         * The query parameter for the view needs to not exclude the assignment and is therefore safely late.
         * @memberOf InsightBillableHoursComponent
         */
        get: function () {
            return new Date(2099, 12, 31, 23, 59, 59);
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Registers the component.
     * @static
     * @memberOf InsightBillableHoursComponent
     */
    InsightBillableHoursComponent.register = function () {
        ComponentRegistry.register('insight', 'InsightBillableHoursComponent', InsightBillableHoursComponent);
    };
    /**
     * Retrieves the assignment key from the URI and obtains the entity.
     * @memberOf InsightBillableHoursComponent
     */
    InsightBillableHoursComponent.prototype.afterInitialize = function () {
        var _this = this;
        this._entityKey = RoutesService.getDetailId(this.router.routerState);
        if (!this._entityKey) {
            return;
        }
        // Checking if the ID in the URL contains more than the OID.
        var index = this._entityKey.indexOf('_');
        if (index > 0) {
            this._entityKey = this._entityKey.slice(0, index);
        }
        var className = DomainService.oidToClassName(this._entityKey);
        if (className === 'SpendTime') {
            // Back-end call not necessary. The information is on the SpendTime itself.
            var entityType = this.domainService.getEntityType('SpendTime');
            // Async/await does not work, not even in a separate method. So the subscription pool is still necessary.
            this.subscriptionPool.add(this.domainService.getEntityByKey(entityType, this._entityKey, true).subscribe(function (spendTime) {
                _this.setBillableValue(moment.duration(spendTime['Effort']).asHours());
            }));
        }
        else {
            _super.prototype.afterInitialize.call(this);
            this.domainService.commitFinishedEvent.subscribe(function (result) {
                result.entities.forEach(function (entity) {
                    if (entity.entityType.shortName === 'SpendTime') {
                        _this.refresh();
                    }
                });
            }).unsubscribe();
        }
    };
    /**
     * Displays the billable hours.
     * @memberOf InsightBillableHoursComponent
     */
    InsightBillableHoursComponent.prototype.afterItemsLoaded = function () {
        var hours = this.loadedItems.length === 0 ? 0 : this.loadedItems[0]['value'];
        this.setBillableValue(hours);
    };
    /**
     * Sets the displayed value.
     * @private
     * @param {number} hours The parameter is multiplied by the billable factor.
     * @memberOf InsightBillableHoursComponent
     */
    InsightBillableHoursComponent.prototype.setBillableValue = function (hours) {
        var spent = moment.duration(this.billableFactor * hours, 'hours');
        var displayValue = this.translateService.instant('APP.INSIGHTS.BILLABLE.CONTENT', { hours: spent.hours() + ":" + addZero(spent.minutes()) });
        this.setData('time', displayValue);
    };
    return InsightBillableHoursComponent;
}(InsightTextComponent));
export { InsightBillableHoursComponent };
